import { cleanTemplateMap } from "../utils/emailBrandTemplate";
import { setLookOnlyUser } from "../utils/lookOnly";

export const actionType = {
  SET_USER: "SET_USER",
  STORE_EMPLOYEES: "STORE_EMPLOYEES",
  UPDATE_USER: "UPDATE_USER",

  STORE_TASKS: "STORE_TASKS",
  STORE_JOBS: "STORE_JOBS",
  STORE_BLOGS: "STORE_BLOGS",
  UPDATE_JOBS: "UPDATE_JOBS",
  STORE_EMPLOYEE_JOBS: "STORE_EMPLOYEE_JOBS",
  UPDATE_ASSIGNEDJOBS: "UPDATE_ASSIGNEDJOBS",
  STORE_CHECKIN: "STORE_CHECKIN",
  STORE_USERS: "STORE_USERS",
  ADD_BLOG: "ADD_BLOG",
  STORE_LEAVES: "STORE_LEAVES",
  UPDATE_LEAVES: "UPDATE_LEAVES",
  STORE_PROJECTS: "STORE_PROJECTS",

  UPDATEUSER: "UPDATEUSER",
  STORE_SELECTED_TILE: "STORE_SELECTED_TILE",
  STORE_PROJECTSUPERVISORS: "STORE_PROJECTSUPERVISORS",

  UPDATEEMPLOYEEDATA: "UPDATEEMPLOYEEDATA",
  SET_STRIPEFORM: "SET_STRIPEFORM",

  STORE_ALLUSERS: "STORE_ALLUSERS",
  STORE_LEADS: "STORE_LEADS",
  STORE_DEALS: "STORE_DEALS",
  // One deal's checklist as just saved ({ dealId, tasks }), put into the deals as they are in the
  // store at that moment. A save can answer minutes after it was made (it waits for the signal,
  // utils/saveWhenConnected.js), so it must never write back a whole list of deals copied earlier.
  PATCH_DEAL_TASKS: "PATCH_DEAL_TASKS",
  // The deals download finished without rows to store (offline, a rules error). The
  // read is over, so screens stop waiting on it; see dealsLoaded in initialState.js.
  DEALS_LOAD_FAILED: "DEALS_LOAD_FAILED",
  STORE_BUYER_LEADS: "STORE_BUYER_LEADS",
  STORE_LISTING_LEADS: "STORE_LISTING_LEADS",
  STORE_LISTING_APPOINTMENTS: "STORE_LISTING_APPOINTMENTS",
  STORE_OPEN_HOUSES: "STORE_OPEN_HOUSES",
  STORE_POSTS: "STORE_POSTS",
  APPEND_POSTS: "APPEND_POSTS",
  ADD_POST: "ADD_POST",
  UPDATE_POST: "UPDATE_POST",
  REMOVE_POST: "REMOVE_POST",
  STORE_COMMENTS_FOR_POST: "STORE_COMMENTS_FOR_POST",
  ADD_COMMENT: "ADD_COMMENT",
  UPDATE_COMMENT: "UPDATE_COMMENT",
  REMOVE_COMMENT: "REMOVE_COMMENT",
  SET_EMAIL_TEMPLATES: "SET_EMAIL_TEMPLATES",
  SET_TEAM_DEFAULTS: "SET_TEAM_DEFAULTS",
};

// Sort helpers so every people/entity dropdown that reads these shared lists is
// alphabetical without patching each dropdown. Always copy — never mutate payloads.
const byFullName = (a, b) =>
  `${a?.firstname || ""} ${a?.lastname || ""}`
    .trim()
    .toLowerCase()
    .localeCompare(
      `${b?.firstname || ""} ${b?.lastname || ""}`.trim().toLowerCase(),
    );
const sortPeople = (arr) =>
  Array.isArray(arr) ? [...arr].sort(byFullName) : arr;
const sortSources = (arr) =>
  Array.isArray(arr)
    ? [...arr].sort((a, b) =>
        String(a?.name || a?.label || "")
          .toLowerCase()
          .localeCompare(String(b?.name || b?.label || "").toLowerCase()),
      )
    : arr;

const dealKey = (d) => String(d?.createdat || d?.id);

// A tick (PATCH_DEAL_TASKS) remembers what that deal's checklist looked like before
// it, for TICK_MEMORY_MS. Home re-downloads every deal each time it opens, and a
// download that began before the tick lands afterwards carrying the old checklist:
// storing it as is made a ticked item come back for staff to redo (Henry,
// 10/06/2026). A downloaded checklist that matches one of those earlier versions is
// stale, so the ticked one stays; anything else is someone's newer change and wins.
const TICK_MEMORY_MS = 10 * 60 * 1000;
const tasksKey = (tasks) => JSON.stringify(Array.isArray(tasks) ? tasks : []);

function logTick(tickLog, id, tasksBefore, now = Date.now()) {
  const log = tickLog || {};
  const entry = log[id] && now - log[id].at < TICK_MEMORY_MS ? log[id] : { before: [] };
  const before = tasksKey(tasksBefore);
  return {
    ...log,
    [id]: { at: now, before: entry.before.includes(before) ? entry.before : [...entry.before, before] },
  };
}

function keepTicksOverStaleDownload(state, incoming, now = Date.now()) {
  const log = state.tickLog;
  if (!log || !Array.isArray(incoming) || !Array.isArray(state.deals)) return { deals: incoming, tickLog: log };
  const nextLog = {};
  const deals = incoming.map((d) => {
    const id = dealKey(d);
    const entry = log[id];
    if (!entry || now - entry.at >= TICK_MEMORY_MS) return d;
    const mine = state.deals.find((x) => dealKey(x) === id);
    if (!mine) return d;
    const got = tasksKey(d?.tasks);
    if (entry.before.includes(got)) {
      nextLog[id] = entry;
      return { ...d, tasks: mine.tasks };
    }
    // The download already has the ticked list: keep remembering, in case an even
    // older download is still on its way.
    if (got === tasksKey(mine.tasks)) nextLog[id] = entry;
    return d;
  });
  return { deals, tickLog: Object.keys(nextLog).length ? nextLog : undefined };
}

const reducer = (state, action) => {
  switch (action.type) {
    case actionType.STORE_DEALS: {
      const kept = keepTicksOverStaleDownload(state, action.deals);
      return {
        ...state,
        deals: kept.deals,
        tickLog: kept.tickLog,
        // Storing deals is the proof the download landed, even when it brought
        // nothing back, so a screen can tell an empty queue from a pending one.
        dealsLoaded: true,
      };
    }

    case actionType.PATCH_DEAL_TASKS: {
      if (!Array.isArray(action.tasks) || !Array.isArray(state.deals)) return state;
      const id = String(action.dealId);
      const current = state.deals.find((d) => dealKey(d) === id);
      if (!current) return state;
      return {
        ...state,
        deals: state.deals.map((d) => (dealKey(d) === id ? { ...d, tasks: action.tasks } : d)),
        tickLog: logTick(state.tickLog, id, current.tasks),
      };
    }

    // Nothing to store, but the waiting is over: keep whatever deals are already in
    // memory and let the screens show what they have (the caller logs the error).
    case actionType.DEALS_LOAD_FAILED:
      return {
        ...state,
        dealsLoaded: true,
      };

    case actionType.STORE_BUYER_LEADS:
      return {
        ...state,
        buyerLeads: action.payload,
      };

    case actionType.STORE_LISTING_LEADS:
      return {
        ...state,
        listingLeads: action.payload,
      };

    case actionType.STORE_OPEN_HOUSES:
      return {
        ...state,
        openHouses: action.payload,
      };

    case actionType.STORE_LEADS:
      return {
        ...state,
        leadsource: sortSources(action.payload),
      };

    case actionType.STORE_ALLUSERS:
      return {
        ...state,
        employees: sortPeople(action.employees),
        clients: sortPeople(action.clients),
        teamleads: sortPeople(action.teamleads),
        users: sortPeople(action.allusers),
      };

    case actionType.SET_STRIPEFORM:
      return {
        ...state,
        stripename: action.name,
        stripeemail: action.email,
      };

    case actionType.UPDATEEMPLOYEEDATA:
      return {
        ...state,
        employees: sortPeople(action.payload),
      };

    case actionType.STORE_PROJECTSUPERVISORS:
      return {
        ...state,
        projectsupervisors: action.payload,
      };

    case actionType.STORE_SELECTED_TILE:
      return {
        ...state,
        selectedtile: action.payload,
      };

    case actionType.ADD_BLOG:
      return {
        ...state,
        blogs: [...action.payload, ...state.blogs],
      };

    case actionType.UPDATE_LEAVES:
      return {
        ...state,
        leaves: action.payload,
      };

    case actionType.UPDATEUSER:
      // The look-only guard (utils/lookOnly.js) always knows who is signed in.
      setLookOnlyUser(action.payload);
      return {
        ...state,
        user: action.payload,
      };

    case actionType.STORE_PROJECTS:
      return {
        ...state,
        projects: action.payload,
      };

    case actionType.STORE_USERS:
      return {
        ...state,
        users: sortPeople(action.payload),
      };
    case actionType.STORE_BLOGS:
      return {
        ...state,
        blogs: action.payload,
      };

    case actionType.STORE_LEAVES:
      return {
        ...state,
        leaves: action.payload,
      };

    case actionType.SET_USER:
      // The look-only guard (utils/lookOnly.js) always knows who is signed in.
      setLookOnlyUser(action.payload);
      return {
        ...state,
        user: action.payload,
      };

    case actionType.UPDATE_USER:
      // The look-only guard (utils/lookOnly.js) always knows who is signed in.
      setLookOnlyUser(action.payload);
      return {
        ...state,
        user: action.payload,
      };

    case actionType.STORE_TASKS:
      return {
        ...state,
        tasks: action.payload,
      };

    case actionType.STORE_JOBS:
      return {
        ...state,
        jobs: action.payload,
      };
    case actionType.UPDATE_JOBS:
      return {
        ...state,
        jobs: action.payload,
      };

    case actionType.STORE_EMPLOYEE_JOBS:
      return {
        ...state,
        assignedjobs: action.payload,
      };

    case actionType.UPDATE_ASSIGNEDJOBS:
      return {
        ...state,
        assignedjobs: action.payload,
      };

    case actionType.STORE_CHECKIN:
      return {
        ...state,
        checkinstatus: action.payload,
      };

    case actionType.STORE_LISTING_APPOINTMENTS:
      return {
        ...state,
        listingAppointments: action.payload,
      };

    case actionType.STORE_POSTS:
      return {
        ...state,
        posts: action.payload.posts,
        postsHasMore: action.payload.hasMore,
        postsCursor: action.payload.nextCursor,
        postsLoading: false,
      };

    case actionType.APPEND_POSTS:
      return {
        ...state,
        posts: [...state.posts, ...action.payload.posts],
        postsHasMore: action.payload.hasMore,
        postsCursor: action.payload.nextCursor,
        postsLoading: false,
      };

    case actionType.ADD_POST:
      return {
        ...state,
        posts: [action.payload, ...state.posts],
      };

    case actionType.UPDATE_POST:
      return {
        ...state,
        posts: state.posts.map((p) =>
          p.id === action.payload.id ? { ...p, ...action.payload } : p,
        ),
      };

    case actionType.REMOVE_POST:
      return {
        ...state,
        posts: state.posts.filter((p) => p.id !== action.payload.id),
      };

    case actionType.STORE_COMMENTS_FOR_POST:
      return {
        ...state,
        postComments: {
          ...state.postComments,
          [action.payload.postId]: action.payload.comments,
        },
      };

    case actionType.ADD_COMMENT: {
      const { postId, comment } = action.payload;
      const existing = state.postComments[postId] || [];
      return {
        ...state,
        postComments: {
          ...state.postComments,
          [postId]: [...existing, comment],
        },
        posts: state.posts.map((p) =>
          p.id === postId
            ? { ...p, commentCount: (p.commentCount || 0) + 1 }
            : p,
        ),
      };
    }

    case actionType.UPDATE_COMMENT: {
      const { postId, comment } = action.payload;
      const existing = state.postComments[postId] || [];
      return {
        ...state,
        postComments: {
          ...state.postComments,
          [postId]: existing.map((c) => (c.id === comment.id ? { ...c, ...comment } : c)),
        },
      };
    }

    case actionType.REMOVE_COMMENT: {
      const { postId, commentId } = action.payload;
      const existing = state.postComments[postId] || [];
      return {
        ...state,
        postComments: {
          ...state.postComments,
          [postId]: existing.filter((c) => c.id !== commentId),
        },
        posts: state.posts.map((p) =>
          p.id === postId
            ? { ...p, commentCount: Math.max(0, (p.commentCount || 0) - 1) }
            : p,
        ),
      };
    }

    case actionType.SET_EMAIL_TEMPLATES:
      // Choke point where saved templates enter the app: clean any pasted-HTML
      // pollution here once, so every screen that reads state.emailTemplates
      // (Settings editor, Email Center resolve(), open-house/acceptance
      // senders) gets readable text without each having to clean it itself.
      return { ...state, emailTemplates: cleanTemplateMap(action.payload || {}) };

    case actionType.SET_TEAM_DEFAULTS:
      return { ...state, teamDefaults: action.payload || null };

    default:
      return state;
  }
};

export default reducer;
